<script setup lang="ts">
import { onMounted, onBeforeMount, watch } from 'vue'
import { useAppStore } from './store/app'
import { useDepositStore } from './store/deposit'
import { useProfileStore } from './store/profile'
import { usePromotionalEventsStore } from './store/promotionalEvents'
import { injectWPAnimationStyles } from './composables/useWhalePointAnimations'
import { storeToRefs } from 'pinia'
import { trackCompleteRegistration, trackSteamLoginSuccess } from './utils/gtagEvent'
import { trackMetaCompleteRegistration } from './utils/metaPixel'

const app = useAppStore()
const profile = useProfileStore()
const deposit = useDepositStore()
const events = usePromotionalEventsStore()
const { profile: profileRef } = storeToRefs(profile)

onBeforeMount(async () => {
  // Start fetching initial data
  await app.fetchInitialData()
  deposit.getCurrencyExchangeRates()

  // Initialize promotional events data after initial data is loaded
  events.initializeEventData()
})

onMounted(() => {
  // Initialize user profile (non-blocking)
  profile.fetchProfile()

  // Inject WP animation styles
  injectWPAnimationStyles()
})

// Fire Steam login success once when user returns with a valid session
watch(profileRef, (curr, prev) => {
  if (!prev && curr && typeof window !== 'undefined') {
    try {
      const started = window.sessionStorage.getItem('steam_login_started')
      const alreadyTracked = window.sessionStorage.getItem('steam_login_success')
      if (started && !alreadyTracked) {
        trackSteamLoginSuccess()
        const createdAtMs = curr.createdAt ? Date.parse(curr.createdAt) : NaN
        const isRecentAccount =
          Number.isFinite(createdAtMs) &&
          createdAtMs <= Date.now() &&
          Date.now() - createdAtMs <= 10 * 60 * 1000
        if (isRecentAccount) {
          // Only fire registration event for newly created accounts.
          trackCompleteRegistration({ provider: 'steam', steam_id: curr.steamId })
          trackMetaCompleteRegistration({ source: 'steam_login' })
        }
        window.sessionStorage.setItem('steam_login_success', '1')
        window.sessionStorage.removeItem('steam_login_started')
      }
    } catch (error) {
      // ignore storage errors
    }
  }
})
</script>

<template>
  <!-- global shell -->
  <div id="app">
    <!-- Router outlet -->
    <router-view />
  </div>
</template>

<style lang="scss"></style>
